import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_BRIDGE_INPUT_DECIMALS,
  OftTypes,
} from 'structure/bridge/constants/bridgeTokens';
import {
  DECIMALS_SHOW,
  NATIVE_FEE_DECIMALS_SHOW,
} from 'structure/bridge/constants/constants';
import {
  DEFAULT_FEE_DECIMALS,
  DEFAULT_TOKEN_DECIMALS,
} from 'structure/bridge/constants/lazyerZeroChains';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  firstDecimals,
  formatBridgeBalanceAmount,
  formatBridgeNativeFeeAmount,
  formatBridgeTokenAmount,
  getMaximumBridgeAmount,
} from 'structure/bridge/utils/bridgeStateHelpers';
import { formatUnits } from 'viem';
import { useConnection } from 'wagmi';
import { defaultNetwork } from '@dapp/multi-lib/network';

interface BridgeUserBalanceAndFeeProps {
  tokenBalance: bigint;
  tokenFee: bigint;
  nativeFee: bigint;
  maxBridgeAmountProxy: bigint;
}

export const BridgeUserBalanceAndFee: React.FC<
  BridgeUserBalanceAndFeeProps
> = ({
  tokenBalance,
  tokenFee,
  nativeFee,
  maxBridgeAmountProxy,
}: BridgeUserBalanceAndFeeProps) => {
  const { t } = useTranslation('common');
  const { chain } = useConnection();
  const chainId = chain?.id ?? defaultNetwork.id;
  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    setRendered(true);
  }, []);

  const onMaxBridgeAmountClick = () => {
    const maximumBridgeAmount = getMaximumBridgeAmount(
      tokenBalance,
      maxBridgeAmountProxy
    );
    const amount = Number(
      formatBridgeTokenAmount(
        maximumBridgeAmount,
        bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS,
        DEFAULT_BRIDGE_INPUT_DECIMALS
      )
    );

    setBridgeState((prevState) => ({
      ...prevState,
      amount: amount,
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
    }));
  };

  const tokenFeeString =
    tokenFee > BigInt(0) && bridgeState.amount > 0
      ? firstDecimals(
          parseFloat(
            formatUnits(
              tokenFee,
              bridgeState?.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
            )
          ),
          DEFAULT_FEE_DECIMALS
        ) +
        ' ' +
        (bridgeState?.token?.symbol ?? '')
      : '0.00' + ' ' + (bridgeState?.token?.symbol ?? '');

  const nativeFeeString =
    nativeFee > BigInt(0) && bridgeState.amount > 0
      ? formatBridgeNativeFeeAmount(nativeFee, NATIVE_FEE_DECIMALS_SHOW) +
        ' ' +
        ((chain?.id && chain?.nativeCurrency?.symbol) ?? 'Gas')
      : '0.00' + ' ' + ((chain?.id && chain?.nativeCurrency?.symbol) ?? 'Gas');

  const destinationOftIsProxy =
    bridgeState.token?.oftType[bridgeState.dstChainId] === OftTypes.Proxy;

  const proxyEnabledAsDestination = bridgeState.token?.enableBridge[chainId];

  return (
    <div className='my-2 flex w-full flex-col items-end text-right'>
      <div className='py-2'>
        <div className='font-haben text-sm font-normal text-white'>
          {t('BRIDGE_FEE')}:{' '}
          <span className='cursor-default text-white'>
            {bridgeState.tokenQuoteLoading
              ? 'Loading...'
              : tokenFeeString + ' + ' + nativeFeeString}
          </span>
        </div>
      </div>

      {rendered && destinationOftIsProxy && proxyEnabledAsDestination && (
        <div className='font-haben pr-2.5 text-sm font-normal text-white'>
          {t('Available in bridge')}:{' '}
          <button
            className='cursor-pointer text-white'
            onClick={onMaxBridgeAmountClick}
          >
            {formatBridgeBalanceAmount(
              maxBridgeAmountProxy,
              bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS,
              DECIMALS_SHOW
            )}{' '}
            {bridgeState.token?.symbol}
          </button>
        </div>
      )}
    </div>
  );
};
